import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  TablePagination,
  CircularProgress,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import SidebarLayout from '../../components/layout/SidebarLayout';
import SetoresService, {
  Setor,
  SetorCreate,
  SetorUpdate,
  SetorFilter,
} from '../../services/setoresService';

// Interface para entidades de referência
interface EntidadeSimples {
  id: number;
  nome: string;
}

const SetoresPage: React.FC = () => {
  const theme = useTheme();
  
  // Estado de listagem
  const [setores, setSetores] = useState<Setor[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Estado de referências
  const [setoresPrincipais, setSetoresPrincipais] = useState<EntidadeSimples[]>([]);
  const [loadingReferencias, setLoadingReferencias] = useState(false);

  // Estado de filtros
  const [filter, setFilter] = useState<SetorFilter>({});
  
  // Estado de formulários
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentSetor, setCurrentSetor] = useState<Setor | null>(null);
  const [newSetor, setNewSetor] = useState<SetorCreate>({ nome: '' });
  const [editSetor, setEditSetor] = useState<SetorUpdate>({});

  // Estado de mensagens
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Adicionar ref para debounce
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Carregar todas as referências necessárias
  const carregarReferencias = useCallback(async () => {
    try {
      setLoadingReferencias(true);
      
      // Buscar setores para uso como "setor principal"
      const setoresData = await SetoresService.getAllSetores();
      setSetoresPrincipais(setoresData);
    } catch (error) {
      console.error('Erro ao carregar referências:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar dados de referência',
        severity: 'error',
      });
    } finally {
      setLoadingReferencias(false);
    }
  }, []);

  const fetchSetores = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      const limit = rowsPerPage;
      
      const data = await SetoresService.getSetores(filter, skip, limit);
      setSetores(data);
      
      // Obter contagem total para paginação
      const count = await SetoresService.getSetoresCount(filter);
      setTotalCount(count);
    } catch (error) {
      console.error('Erro ao buscar setores:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao buscar setores',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter]);

  useEffect(() => {
    // Buscar setores ao iniciar a página ou mudar filtros/paginação
    fetchSetores();
    // Carregar referências
    carregarReferencias();
  }, [fetchSetores, carregarReferencias]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof SetorFilter, value: string | number | undefined) => {
    // Limpar timeout anterior se existir
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Atualizar o filtro imediatamente na UI
    setFilter((prev) => ({
      ...prev,
      [field]: value || undefined, // Use undefined para remover o filtro vazio
    }));

    // Debounce: aguardar 500ms antes de aplicar a busca
    searchTimeoutRef.current = setTimeout(() => {
      setPage(0); // Resetar paginação ao mudar filtro
    }, 500);
  };

  // Função para limpar filtro de busca
  const clearSearchFilter = () => {
    setFilter((prev) => ({
      ...prev,
      nome: undefined
    }));
    setPage(0);
  };

  const handleCreateSetor = async () => {
    try {
      // Verificar campos obrigatórios
      if (!newSetor.nome) {
        setSnackbar({
          open: true,
          message: 'Nome do setor é obrigatório',
          severity: 'error',
        });
        return;
      }

      const result = await SetoresService.createSetor(newSetor);
      setOpenCreateDialog(false);
      setSnackbar({
        open: true,
        message: 'Setor criado com sucesso',
        severity: 'success',
      });
      
      // Limpar formulário
      setNewSetor({ nome: '' });
      
      // Recarregar dados
      fetchSetores();
      
      // Atualizar setores principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao criar setor:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar setor',
        severity: 'error',
      });
    }
  };

  const handleOpenEditDialog = (setor: Setor) => {
    setCurrentSetor(setor);
    // Inicializar dados para edição
    setEditSetor({
      nome: setor.nome,
      principal_id: setor.principal_id
    });
    
    setOpenEditDialog(true);
  };

  const handleEditSetor = async () => {
    if (!currentSetor) return;

    try {
      // Verificar campos obrigatórios
      if (!editSetor.nome) {
        setSnackbar({
          open: true,
          message: 'Nome do setor é obrigatório',
          severity: 'error',
        });
        return;
      }

      await SetoresService.updateSetor(currentSetor.id, editSetor);
      setOpenEditDialog(false);
      setSnackbar({
        open: true,
        message: 'Setor atualizado com sucesso',
        severity: 'success',
      });
      
      // Limpar estados
      setCurrentSetor(null);
      setEditSetor({});
      
      // Recarregar dados
      fetchSetores();
      
      // Atualizar setores principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao atualizar setor:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar setor',
        severity: 'error',
      });
    }
  };

  const handleOpenDeleteDialog = (setor: Setor) => {
    setCurrentSetor(setor);
    setOpenDeleteDialog(true);
  };

  const handleDeleteSetor = async () => {
    if (!currentSetor) return;

    try {
      await SetoresService.deleteSetor(currentSetor.id);
      setOpenDeleteDialog(false);
      setSnackbar({
        open: true,
        message: 'Setor excluído com sucesso',
        severity: 'success',
      });
      
      // Limpar estados
      setCurrentSetor(null);
      
      // Recarregar dados
      fetchSetores();
      
      // Atualizar setores principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao excluir setor:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao excluir setor',
        severity: 'error',
      });
    }
  };

  // Handler para alterações em selects
  const handleSelectChange = (event: SelectChangeEvent<number | string | undefined>, setter: Function) => {
    const value = event.target.value === '' ? undefined : Number(event.target.value);
    setter((prev: any) => ({
      ...prev,
      [event.target.name]: value
    }));
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  return (
    <SidebarLayout>
      <Box p={3}>
        <Typography variant="h4" gutterBottom display="flex" alignItems="center">
          <CategoryIcon sx={{ mr: 2 }} />
          Gerenciamento de Setores
        </Typography>
        
        {/* Área de filtros */}
        <Paper 
          elevation={2} 
          sx={{ p: 2, mb: 3, backgroundColor: theme.palette.background.default }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Buscar por nome"
                variant="outlined"
                size="small"
                value={filter.nome || ''}
                onChange={(e) => handleFilterChange('nome', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: filter.nome ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={clearSearchFilter}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="setor-principal-filter-label">Setor Principal</InputLabel>
                <Select
                  labelId="setor-principal-filter-label"
                  label="Setor Principal"
                  name="principal_id"
                  value={filter.principal_id || ''}
                  onChange={(e) => handleFilterChange('principal_id', e.target.value === '' ? undefined : Number(e.target.value))}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {setoresPrincipais.map((setor) => (
                    <MenuItem key={setor.id} value={setor.id}>
                      {setor.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenCreateDialog(true)}
                sx={{ height: '100%' }}
              >
                Novo Setor
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabela de Setores */}
        <Paper elevation={3} sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 280px)' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Setor Principal</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : setores.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Nenhum setor encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  setores.map((setor) => (
                    <TableRow key={setor.id} hover>
                      <TableCell>{setor.id}</TableCell>
                      <TableCell>{setor.nome}</TableCell>
                      <TableCell>
                        {setoresPrincipais.find(s => s.id === setor.principal_id)?.nome || '-'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEditDialog(setor)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(setor)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      {/* Modal de Criação */}
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Novo Setor</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={newSetor.nome || ''}
                onChange={(e) => setNewSetor({ ...newSetor, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="new-setor-principal-label">Setor Principal</InputLabel>
                <Select
                  labelId="new-setor-principal-label"
                  label="Setor Principal"
                  name="principal_id"
                  value={newSetor.principal_id || ''}
                  onChange={(e) => handleSelectChange(e, setNewSetor)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {setoresPrincipais.map((setor) => (
                    <MenuItem key={setor.id} value={setor.id}>
                      {setor.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateSetor}
            disabled={!newSetor.nome}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Edição */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Editar Setor</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={editSetor.nome || ''}
                onChange={(e) => setEditSetor({ ...editSetor, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="edit-setor-principal-label">Setor Principal</InputLabel>
                <Select
                  labelId="edit-setor-principal-label"
                  label="Setor Principal"
                  name="principal_id"
                  value={editSetor.principal_id || ''}
                  onChange={(e) => handleSelectChange(e, setEditSetor)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {setoresPrincipais
                    .filter(setor => currentSetor ? setor.id !== currentSetor.id : true)
                    .map((setor) => (
                      <MenuItem key={setor.id} value={setor.id}>
                        {setor.nome}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleEditSetor}
            disabled={!editSetor.nome}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o setor "{currentSetor?.nome}"?
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDeleteSetor}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SidebarLayout>
  );
};

export default SetoresPage;
